import {useConfig} from '@aboutyou/config';
import {SoldoutProductTile} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/soldout_product_tile.types';
import {useTranslation} from '@aboutyou/i18n';
import {
  TrackProduct,
  useTrackFeature,
  useTrackFeatureInViewImpression,
  useTrackWishlist,
} from '@aboutyou/tracking';
import {
  Button,
  FONT_WEIGHTS,
  LoadingSpinner,
  ProductImage,
  UnstyledLink,
  Z_INDEXES,
  mediaDesktopAndUp,
  useShowToast,
} from '@aboutyou/ui-components';
import React, {useState} from 'react';
import styled, {css} from 'styled-components';
import {useSimilarProductsPageOriginClick} from '../../SimilarProducts/hooks/useSimilarProductsPageOriginClick';
import {RemoveItemButton} from '../WishlistProductTile/RemoveItemButton';
import {RemoveItemConfirmModal} from '../WishlistProductTile/RemoveItemConfirmModal';

interface WishlistSoldoutProductTileProps {
  tile: SoldoutProductTile;
  itemKey: string;
  streamPosition: number;
  onClickSoldoutCta: (productId: number) => void;
  showConfirmOnDeleteItem?: boolean;
  gridColumns?: number;
}

export const WishlistSoldoutProductTile: React.FC<
  WishlistSoldoutProductTileProps
> = ({tile, streamPosition, onClickSoldoutCta, showConfirmOnDeleteItem}) => {
  const {t} = useTranslation();
  const isMobile = useConfig().device === 'mobile';
  const showToast = useShowToast();
  const [isSpinnerVisible, setIsSpinnerVisible] = useState(false);
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
  const trackSimilarProductsOriginClick = useSimilarProductsPageOriginClick();
  const trackFeature = useTrackFeature();
  const {ref: trackSimilarProductsFeatureRef} = useTrackFeatureInViewImpression(
    {label: 'WL_similar_products'},
  );

  const tracker = useTrackWishlist({
    product: tile.productTracker,
    price: undefined,
    brand: tile.brandTracker,
    streamPosition,
    trackingData: {blockTypeAndId: 'product_stream'},
    badges: [],
  });

  const handleClickRemoveItemSuccess = () => {
    tracker('removed-from-wishlist');
  };

  const handleClickRemoveItemError = () => {
    setIsSpinnerVisible(false);
    setIsConfirmModalVisible(false);
    showToast({
      content: t('Oops! Something went wrong!'),
      type: 'ERROR',
      'data-testid': 'RemoveFromWishlistButtonToast',
    });
  };

  const handleClickRemoveItemButton = () => setIsSpinnerVisible(true);

  const similarProductsUrl = `${tile.link.url}/similar`;

  const actionButtons = (
    <Actions data-testid="wishlistSoldoutProductTileActions">
      <Button
        variant="secondary"
        data-testid="soldOutRemindMeButton"
        onClick={(e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          onClickSoldoutCta(tile.productId);
        }}
      >
        {t('Remind me')}
      </Button>
      <Button
        ref={trackSimilarProductsFeatureRef}
        variant="secondary"
        data-testid="similarProductsButtonSoldOut"
        href={similarProductsUrl}
        openLinkInNewTab={tile.link.external}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          trackFeature({eventType: 'click', label: 'WL_similar_products'});
          trackSimilarProductsOriginClick();
        }}
      >
        {t('Similar Products')}
      </Button>
    </Actions>
  );

  const tileContent = (
    <>
      <ProductImage
        src={tile.image.src}
        alt={tile.image.description}
        ratio="3:4"
        padded={tile.image.isEnclosed}
        increasePadding
        dataTestId="productImage"
      >
        <SoldOutOverlay data-testid="wishlistSoldoutProductTileSoldOutLabel">
          <SoldOutText data-testid="soldOutProductText">
            {t('Sold Out')}
          </SoldOutText>
          {!isMobile && actionButtons}
        </SoldOutOverlay>
      </ProductImage>
      <ContentWrapper>
        {tile.brandName && (
          <BrandName data-testid="wishlistSoldoutProductTileBrand">
            {tile.brandName}
          </BrandName>
        )}
        {isMobile && actionButtons}
      </ContentWrapper>
    </>
  );

  return (
    <TrackProduct
      Comp="li"
      productTile={{
        product: tile.productTracker,
        brand: tile.brandTracker,
        price: undefined,
      }}
      streamPosition={streamPosition}
      trackingKey={tile.productId}
      trackingData={{blockTypeAndId: 'product_stream'}}
      threshold={0.2}
      triggerOnce
      badges={[]}
      data-testid={`productTileTracker-${tile.productId}`}
    >
      <Wrapper data-testid="productTile">
        <TileLink
          as={UnstyledLink}
          to={tile.link.url}
          aria-label={tile.image.description}
        >
          {tileContent}
        </TileLink>
        <RemoveButtonWrapper>
          <RemoveItemButton
            iconType="trash"
            productId={tile.productId}
            onClickRemoveItem={handleClickRemoveItemButton}
            onClickRemoveItemSuccess={handleClickRemoveItemSuccess}
            onClickRemoveItemError={handleClickRemoveItemError}
            onShowConfirm={
              showConfirmOnDeleteItem
                ? () => setIsConfirmModalVisible(true)
                : undefined
            }
          />
        </RemoveButtonWrapper>
        {isSpinnerVisible && !showConfirmOnDeleteItem && (
          <FullSizeSpinner
            data-testid="WishlistSoldoutTileFullSizeSpinner"
            visible
          >
            <LoadingSpinner />
          </FullSizeSpinner>
        )}
        <RemoveItemConfirmModal
          isOpen={isConfirmModalVisible}
          onClose={() => setIsConfirmModalVisible(false)}
          onClickConfirm={handleClickRemoveItemButton}
          onClickRemoveItemSuccess={handleClickRemoveItemSuccess}
          onClickRemoveItemError={handleClickRemoveItemError}
          isLoading={isSpinnerVisible}
          productId={tile.productId}
        />
      </Wrapper>
    </TrackProduct>
  );
};

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1 1;
`;

const Wrapper = styled.div`
  position: relative;
  border-radius: 4px;
  overflow: hidden;
  height: 100%;
`;

const TileLink = styled.div`
  color: inherit;
  text-decoration: none;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const SoldOutOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.6);
  color: #000;

  @media ${mediaDesktopAndUp} {
    justify-content: flex-end;
    padding-bottom: 20%;
  }
`;

const SoldOutText = styled.span`
  font-size: 12px;
  font-weight: ${FONT_WEIGHTS.w500};
  margin-bottom: 10px;
`;

const BrandName = styled.p`
  margin: 0;
  padding: 12px 8px;
  font-size: 10px;
  font-weight: ${FONT_WEIGHTS.w500};
  line-height: 1.8;
  text-align: center;
  text-transform: uppercase;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  letter-spacing: 0.4px;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  padding: 16px 8px;

  @media ${mediaDesktopAndUp} {
    width: calc(100% - 24px);
    max-width: 220px;
    padding: 0;
  }
`;

const RemoveButtonWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  z-index: ${Z_INDEXES.PRODUCT_TILE_CTA};
  display: flex;
  padding: 15px;

  @media ${mediaDesktopAndUp} {
    padding: 23px;
  }
`;

const FullSizeSpinner = styled.div<{visible: boolean}>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: none;
  pointer-events: none;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: -1;
  opacity: 0;
  transition: opacity 300ms ease;

  ${({visible}) =>
    visible &&
    css`
      opacity: 1;
      background: rgba(255, 255, 255, 0.5);
      pointer-events: auto;
      z-index: 1;
    `}
`;
